
import { useState, useRef, Suspense } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from "@/app/components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import {
  CostingSummary, EventHeader, ExpensesSection, KeyMetrics,
  PnLSummary, RevenueSection, SectionCard, VendorAllocations,
  type AnyReportData, type LimitedReportData, type ReportData,
} from "./components";
import { fmt, fmtDate } from "./components/types";
import { formatDate, formatDateTime } from "@/lib/dateFormat";
import { BarChart3, DollarSign, Package, Receipt, TrendingUp, Printer, Ban } from "lucide-react";
import { ReportError, useReport } from "../../ReportKit";

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-busy="true">
      <div className="h-20 bg-stone-100 rounded-2xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-stone-100 rounded-2xl" />)}
      </div>
      <div className="h-48 bg-stone-100 rounded-2xl" />
      <div className="h-40 bg-stone-100 rounded-2xl" />
    </div>
  );
}

// ─── Print ────────────────────────────────────────────────────────────────────
// Print-only layouts, hidden on screen; handlePrint makes only #print-report visible.

const cellL: React.CSSProperties = { padding: "5px 10px", borderBottom: "1px solid #f5f5f4", fontSize: 11 };
const cellR: React.CSSProperties = { ...cellL, textAlign: "right" };
const headL: React.CSSProperties = { padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "#78716c", background: "#fafaf9", borderBottom: "1px solid #e7e5e4", textAlign: "left" };
const headR: React.CSSProperties = { ...headL, textAlign: "right" };
const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: "#1c1917", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e7e5e4" };

function PrintShell({ title, event, projected, children }: {
  title: string; event: AnyReportData["event"]; projected?: boolean; children: React.ReactNode;
}) {
  return (
    <div id="print-report" style={{ display: "none", fontFamily: "system-ui, sans-serif", color: "#1c1917", background: "#fff", padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, borderBottom: "2px solid #e7e5e4", paddingBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#92400e" }}>Shalimar Events</div>
          <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>{title}</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 11, color: "#78716c" }}>
          <div>Printed: {formatDate(new Date())}</div>
          {projected && <div style={{ color: "#d97706", fontWeight: 600, marginTop: 4 }}>Projected figures</div>}
        </div>
      </div>
      <div style={{ background: "#fafaf9", border: "1px solid #e7e5e4", borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{event.name}</div>
        <div style={{ fontSize: 12, color: "#78716c", marginTop: 4 }}>
          {[event.client, event.event_type, event.venue, event.status?.replace(/_/g, " ")].filter(Boolean).join(" · ")}
        </div>
        <div style={{ fontSize: 11, color: "#44403c", marginTop: 6 }}>
          {event.start_date && fmtDate(event.start_date)}
          {event.end_date && event.end_date !== event.start_date && ` – ${fmtDate(event.end_date)}`}
          {event.guest_count > 0 && ` · ${event.guest_count} guests`}
        </div>
      </div>
      {children}
      <div style={{ marginTop: 32, paddingTop: 12, borderTop: "1px solid #e7e5e4", fontSize: 10, color: "#a8a29e", display: "flex", justifyContent: "space-between" }}>
        <span>Shalimar Events — Confidential</span>
        <span>Generated on {formatDateTime(new Date())}</span>
      </div>
    </div>
  );
}

function PaymentsTable({ payments }: { payments: AnyReportData["client_payments"] }) {
  if (payments.length === 0) return <div style={{ fontSize: 11, color: "#a8a29e" }}>No payments recorded yet.</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
      <thead><tr>{["Date", "Method", "Reference", "Notes"].map((h) => <th key={h} style={headL}>{h}</th>)}<th style={headR}>Amount</th></tr></thead>
      <tbody>
        {payments.map((p) => (
          <tr key={p.id}>
            <td style={cellL}>{fmtDate(p.payment_date)}</td>
            <td style={{ ...cellL, textTransform: "capitalize" }}>{p.payment_method?.replace(/_/g, " ") ?? "—"}</td>
            <td style={cellL}>{p.reference_number ?? "—"}</td>
            <td style={cellL}>{p.notes ?? "—"}</td>
            <td style={{ ...cellR, fontWeight: 700, color: "#059669" }}>{fmt(p.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PrintReport({ data }: { data: ReportData }) {
  const { event, financials: f, vendor_detail, service_detail, client_payments, expenses, is_projected } = data;
  const profit = f.profit ?? 0;
  const cost = f.total_expense ?? f.total_cost + f.adhoc_cost;
  const margin = f.net_revenue > 0 ? ((profit / f.net_revenue) * 100).toFixed(1) : "0.0";

  return (
    <PrintShell title="Event Performance Report" event={event} projected={is_projected}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Revenue (before GST)", value: fmt(f.net_revenue), sub: `+ GST ${fmt(f.tax_amount)} = billed ${fmt(f.final_revenue)}` },
          { label: "Cost", value: fmt(cost), sub: "services, vendors and expenses" },
          { label: profit >= 0 ? "Profit" : "Loss", value: fmt(Math.abs(profit)), sub: `${margin}% margin` },
          { label: "Client still owes", value: fmt(f.customer?.balance ?? 0), sub: `Received ${fmt(f.customer?.paid ?? 0)}` },
        ].map((m) => (
          <div key={m.label} style={{ border: "1px solid #e7e5e4", borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 10, color: "#78716c", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{m.value}</div>
            <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>Client payments</div>
        <PaymentsTable payments={client_payments} />
      </div>

      {(vendor_detail.length > 0 || service_detail.length > 0) && (
        <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
          <div style={sectionTitle}>Items</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr><th style={headL}>Item</th><th style={headR}>Qty</th><th style={headR}>Charged</th><th style={headR}>Cost</th><th style={headR}>Profit</th></tr></thead>
            <tbody>
              {service_detail.map((s, i) => (
                <tr key={`s${i}`}>
                  <td style={cellL}>{s.name}{s.vendor_name ? ` (${s.vendor_name})` : ""}</td>
                  <td style={cellR}>{s.quantity}</td>
                  <td style={cellR}>{fmt(s.revenue)}</td>
                  <td style={cellR}>{fmt(s.cost)}</td>
                  <td style={cellR}>{fmt(s.revenue - s.cost)}</td>
                </tr>
              ))}
              {vendor_detail.map((v) => (
                <tr key={`v${v.id}`}>
                  <td style={cellL}>{v.vendor_name}{v.description ? ` — ${v.description}` : ""}</td>
                  <td style={cellR}>{v.quantity}</td>
                  <td style={cellR}>{fmt(v.billed)}</td>
                  <td style={cellR}>{fmt(v.total_amount)}</td>
                  <td style={cellR}>{fmt(v.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expenses.length > 0 && (
        <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
          <div style={sectionTitle}>Other expenses</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr>{["Title", "Category", "Date", "Paid to"].map((h) => <th key={h} style={headL}>{h}</th>)}<th style={headR}>Amount</th></tr></thead>
            <tbody>
              {expenses.map((e, i) => (
                <tr key={i}>
                  <td style={cellL}>{e.title}</td>
                  <td style={cellL}>{e.category}</td>
                  <td style={cellL}>{fmtDate(e.expense_date)}</td>
                  <td style={cellL}>{e.paid_to ?? "—"}</td>
                  <td style={cellR}>{fmt(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ pageBreakInside: "avoid" }}>
        <div style={sectionTitle}>Profit &amp; loss</div>
        <table style={{ width: "100%", maxWidth: 420, borderCollapse: "collapse", fontSize: 11 }}>
          <tbody>
            {[
              ["Subtotal", fmt(f.income_subtotal)],
              ["Less discount", `– ${fmt(f.discount_amount)}`],
              ["Revenue (before GST)", fmt(f.net_revenue)],
              ["Less cost", `– ${fmt(cost)}`],
              [profit >= 0 ? "Profit" : "Loss", fmt(Math.abs(profit))],
              ["GST collected (not income)", fmt(f.tax_amount)],
            ].map(([label, value]) => (
              <tr key={label}><td style={{ padding: "4px 0" }}>{label}</td><td style={{ padding: "4px 0", textAlign: "right", fontWeight: 600 }}>{value}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </PrintShell>
  );
}

function LimitedPrint({ data }: { data: LimitedReportData }) {
  return (
    <PrintShell title="Event Report" event={data.event}>
      <div style={{ display: "flex", gap: 24, marginBottom: 20, fontSize: 12 }}>
        <div><b>Received:</b> {fmt(data.customer.paid)}</div>
        <div><b>Still owed:</b> {fmt(data.customer.balance)}</div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>Client payments</div>
        <PaymentsTable payments={data.client_payments} />
      </div>
      <BookedItemsTable data={data} print />
    </PrintShell>
  );
}

// ─── Limited view (roles without money access) ────────────────────────────────

function BookedItemsTable({ data, print = false }: { data: LimitedReportData; print?: boolean }) {
  const items = [
    ...data.services.map((s) => ({ name: s.name, who: s.vendor_name, qty: s.quantity })),
    ...data.vendors.map((v) => ({ name: v.description || v.vendor_type || "Vendor work", who: v.vendor_name, qty: v.quantity })),
  ];
  if (items.length === 0) return print ? null : <p className="text-xs text-stone-400 italic">Nothing booked yet.</p>;
  if (print) {
    return (
      <div>
        <div style={sectionTitle}>Services and vendors</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead><tr><th style={headL}>Item</th><th style={headL}>Vendor</th><th style={headR}>Qty</th></tr></thead>
          <tbody>{items.map((it, i) => <tr key={i}><td style={cellL}>{it.name}</td><td style={cellL}>{it.who ?? "—"}</td><td style={cellR}>{it.qty}</td></tr>)}</tbody>
        </table>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-stone-100">
      {items.map((it, i) => (
        <li key={i} className="flex items-center justify-between gap-3 py-2.5 text-sm">
          <div className="min-w-0">
            <p className="font-medium text-stone-800 truncate">{it.name}</p>
            {it.who && <p className="text-xs text-stone-500 truncate">{it.who}</p>}
          </div>
          <span className="text-xs text-stone-500 shrink-0">Qty {it.qty}</span>
        </li>
      ))}
    </ul>
  );
}

function LimitedView({ data }: { data: LimitedReportData }) {
  const { customer, client_payments } = data;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 sm:p-4">
          <p className="text-xs font-semibold uppercase text-emerald-600">Received</p>
          <p className="text-lg sm:text-xl font-bold text-emerald-700 tabular-nums">{fmt(customer.paid)}</p>
        </div>
        <div className={`rounded-xl border p-3 sm:p-4 ${customer.balance > 0 ? "border-amber-200 bg-amber-50" : "border-stone-200 bg-white"}`}>
          <p className="text-xs font-semibold uppercase text-stone-500">Still owed</p>
          <p className={`text-lg sm:text-xl font-bold tabular-nums ${customer.balance > 0 ? "text-amber-700" : "text-stone-500"}`}>{fmt(customer.balance)}</p>
        </div>
      </div>
      <SectionCard title={`Client payments (${client_payments.length})`} icon={<DollarSign className="w-4 h-4" />}>
        {client_payments.length === 0 ? (
          <p className="text-xs text-stone-400 italic">No payments recorded yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full min-w-[460px] text-xs">
              <thead className="bg-stone-50">
                <tr>{["Date", "Method", "Reference", "Amount"].map((h) => <th key={h} className={`px-3 py-2 text-stone-500 font-semibold uppercase ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {client_payments.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">{fmtDate(p.payment_date)}</td>
                    <td className="px-3 py-2.5 text-stone-600 capitalize">{p.payment_method?.replace(/_/g, " ") ?? "—"}</td>
                    <td className="px-3 py-2.5 text-stone-500">{p.reference_number ?? "—"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-emerald-700 whitespace-nowrap">{fmt(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      <SectionCard title="Services and vendors booked" icon={<Package className="w-4 h-4" />}>
        <BookedItemsTable data={data} />
      </SectionCard>
    </div>
  );
}

// ─── Tab Nav ──────────────────────────────────────────────────────────────────
type Tab = "overview" | "financial" | "vendors" | "expenses" | "profitability";

function TabNavigation({ activeTab, onTabChange, tabs }: {
  activeTab: Tab;
  onTabChange: (t: Tab) => void;
  tabs: { id: Tab; label: string; icon: React.ReactNode }[];
}) {
  return (
    <div className="bg-white border-b border-stone-200 rounded-t-xl overflow-x-auto">
      <div className="flex gap-1 p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-2 px-3 sm:px-4 h-10 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-amber-100 text-amber-700 border border-amber-300"
                : "text-stone-600 hover:bg-stone-50 border border-transparent"
            }`}
          >
            <span className="w-4 h-4">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MoneyView({ data }: { data: ReportData }) {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const { financials, vendor_detail, vendor_totals, service_totals, service_detail, client_payments, expenses } = data;
  const tabs = [
    { id: "overview" as Tab, label: "Overview", icon: <BarChart3 className="w-4 h-4" /> },
    { id: "financial" as Tab, label: "Money", icon: <DollarSign className="w-4 h-4" /> },
    { id: "vendors" as Tab, label: "Items", icon: <Package className="w-4 h-4" /> },
    { id: "expenses" as Tab, label: "Expenses", icon: <Receipt className="w-4 h-4" /> },
    { id: "profitability" as Tab, label: "P&L", icon: <TrendingUp className="w-4 h-4" /> },
  ];

  return (
    <>
      <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
        <KeyMetrics financials={financials} vendorTotals={vendor_totals} serviceTotals={service_totals} />
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm">
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />
        <div className="p-4 sm:p-6">
          {activeTab === "overview" && (
            <div className="space-y-4">
              <RevenueSection financials={financials} clientPayments={client_payments} />
              <VendorAllocations vendorDetail={vendor_detail} serviceDetail={service_detail} />
            </div>
          )}
          {activeTab === "financial" && (
            <div className="space-y-4">
              <RevenueSection financials={financials} clientPayments={client_payments} />
              <CostingSummary financials={financials} vendorTotals={vendor_totals} serviceTotals={service_totals} expenses={expenses} />
            </div>
          )}
          {activeTab === "vendors" && <VendorAllocations vendorDetail={vendor_detail} serviceDetail={service_detail} />}
          {activeTab === "expenses" && <ExpensesSection expenses={expenses} />}
          {activeTab === "profitability" && <PnLSummary financials={financials} vendorTotals={vendor_totals} serviceTotals={service_totals} />}
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function EventDetailReportPageContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = Number(searchParams.get("id"));
  const styleRef = useRef<HTMLStyleElement | null>(null);

  const state = useReport<AnyReportData>(
    async () => (await reportsApi.eventDetailReport(eventId)).data,
    [eventId],
    eventId > 0,
  );

  const handlePrint = () => {
    const printEl = document.getElementById("print-report");
    if (printEl) printEl.style.display = "block";

    const style = document.createElement("style");
    style.id = "print-override-css";
    style.innerHTML = `
      @media print {
        body * { visibility: hidden !important; }
        #print-report, #print-report * { visibility: visible !important; }
        #print-report { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; background: white !important; z-index: 99999 !important; }
        @page { margin: 1cm; size: A4 portrait; }
      }
    `;
    document.head.appendChild(style);
    styleRef.current = style;

    window.onafterprint = () => {
      if (styleRef.current) {
        document.head.removeChild(styleRef.current);
        styleRef.current = null;
      }
      if (printEl) printEl.style.display = "none";
      window.onafterprint = null;
    };

    window.print();
  };

  if (!eventId) {
    return (
      <DashboardLayout>
        <ReportError message="No event was chosen. Open an event from the Events report." onRetry={() => navigate("/reports/events")} />
      </DashboardLayout>
    );
  }
  if (state.loading) {
    return <DashboardLayout><Skeleton /></DashboardLayout>;
  }
  if (state.error || !state.data) {
    return (
      <DashboardLayout>
        <ReportError message={state.error ?? "No data came back."} onRetry={state.retry} />
      </DashboardLayout>
    );
  }

  const data = state.data;
  const cancelled = data.event.status === "cancelled";

  return (
    <DashboardLayout>
      {data.can_view_money ? <PrintReport data={data} /> : <LimitedPrint data={data} />}

      <div className="flex flex-col gap-4 sm:gap-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate(-1)} className="h-10 flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </button>
          <button onClick={handlePrint} className="h-10 flex items-center gap-2 text-sm font-semibold text-white px-4 rounded-xl bg-amber-600 hover:bg-amber-700">
            <Printer className="w-4 h-4" /> Print Report
          </button>
        </div>

        <EventHeader event={data.event} isProjected={data.can_view_money ? data.is_projected : false} />

        {cancelled && (
          <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <Ban className="w-4 h-4 mt-0.5 shrink-0" />
            <span>This event is cancelled: reports don’t count it as revenue or profit, and the client owes nothing more. Payments already made still count as received.</span>
          </div>
        )}

        {data.can_view_money ? <MoneyView data={data} /> : <LimitedView data={data} />}
      </div>
    </DashboardLayout>
  );
}

export default function EventDetailReportPage() {
  return (
    <Suspense fallback={<DashboardLayout><Skeleton /></DashboardLayout>}>
      <EventDetailReportPageContent />
    </Suspense>
  );
}
